import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SITE, IMAGES, SEGMENTS } from "../data/site";
import { PRODUCTS } from "../data/products";
import { useLang } from "../i18n";
import { MaskedLine, Reveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { SmartImage } from "../components/SmartImage";
import { CatalogueButton } from "../components/CatalogueButton";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.hero} alt="Sana Steel scaffolding on a construction site at sunset"
             className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-navy/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-32 pt-40 w-full">
        <MaskedLine delay={0.1}>
          <span className="section-eyebrow-dark inline-flex items-center gap-3">
            <span className="w-8 h-[2px] bg-silver inline-block" />
            {t("hero.eyebrow")}
          </span>
        </MaskedLine>
        <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-white leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem]">
          <MaskedLine delay={0.25}>{t("hero.l1")}</MaskedLine>
          <MaskedLine delay={0.4}>{t("hero.l2")}</MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="text-silver">{t("hero.l3")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.75}>
          <p className="mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">{t("hero.sub")}</p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/quote" data-testid="hero-quote-button" className="btn-light">
            {t("hero.cta1")} <ArrowRight size={16} />
          </Link>
          <Link to="/products" data-testid="hero-products-button" className="btn-outline-navy">
            {t("hero.cta2")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const PILLAR_KEYS = ["p1", "p2", "p3", "p4", "p5"];

const Pillars = () => {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Reveal>
            <p className="section-eyebrow">{t("pillars.eyebrow")}</p>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-black uppercase tracking-tight text-navy leading-[1.02]">
              {t("pillars.h2")}
            </h2>
            <p className="mt-6 text-steel text-base leading-relaxed">{t("pillars.sub")}</p>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          {PILLAR_KEYS.map((k, i) => (
            <Reveal key={k} delay={i * 0.06}>
              <div className="group border-t border-gray-200 py-8 flex gap-6 md:gap-10 items-start transition-colors duration-300 hover:bg-surface px-2 md:px-4"
                   data-testid={`pillar-0${i + 1}`}>
                <span className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-gray-200 group-hover:text-steel transition-colors duration-300 leading-none">
                  0{i + 1}
                </span>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-black uppercase tracking-tight text-navy">
                    {t(`${k}.t`)}
                  </h3>
                  <p className="mt-2 text-steel text-sm md:text-base leading-relaxed max-w-lg">{t(`${k}.d`)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Highlights = () => {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-32 bg-surface" data-testid="product-highlights-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <p className="section-eyebrow">{t("hl.eyebrow")}</p>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-black uppercase tracking-tight text-navy">
              {t("hl.h2")}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <CatalogueButton testId="home-download-catalogue" />
            <Link to="/products" data-testid="highlights-view-all-link"
                  className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-navy inline-flex items-center gap-2 hover:text-steel transition-colors duration-200">
              {t("hl.all")} <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7" delay={0}>
            <ProductCard product={PRODUCTS[0]} index={0} />
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.08}>
            <ProductCard product={PRODUCTS[3]} index={3} />
          </Reveal>
          <Reveal className="md:col-span-5" delay={0}>
            <ProductCard product={PRODUCTS[2]} index={2} />
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.08}>
            <Link to="/quote" data-testid="highlights-cta-card"
                  className="group bg-navy h-full min-h-[320px] flex flex-col justify-between p-8 md:p-12 transition-shadow duration-300 hover:shadow-[6px_6px_0_#7C828B]">
              <p className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tighter text-white leading-[1.02]">
                {t("hl.cta1a")} <span className="text-silver">{t("hl.cta1b")}</span>
              </p>
              <span className="mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.2em] text-white group-hover:text-silver transition-colors duration-200">
                {t("hl.cta2")} <ArrowRight size={18} />
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const IdealFor = () => {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="ideal-for-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="mb-14">
          <p className="section-eyebrow">{t("ideal.eyebrow")}</p>
          <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-black uppercase tracking-tight text-navy">
            {t("ideal.h2")}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {SEGMENTS.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.06}>
              <Link to="/quote" data-testid={`segment-${s.key}`}
                    className="group block relative overflow-hidden h-80 border border-gray-200">
                <SmartImage src={s.image} alt={t(`seg.${s.key}.name`)}
                            className="absolute inset-0 w-full h-full object-cover grayscale-[70%] transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                <div className="absolute inset-0 bg-navy/60 transition-colors duration-500 group-hover:bg-navy/30" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-heading text-xs font-bold text-silver">0{i + 1}</span>
                  <h3 className="font-heading text-xl font-black uppercase tracking-tight text-white mt-1 leading-tight">
                    {t(`seg.${s.key}.name`)}
                  </h3>
                  <p className="mt-2 text-xs text-white/70 leading-relaxed max-h-0 overflow-hidden transition-[max-height] duration-500 group-hover:max-h-24">
                    {t(`seg.${s.key}.desc`)}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="bg-navy py-24 md:py-32" data-testid="cta-band-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="section-eyebrow-dark">{t("ct.eyebrow")}</p>
          <h2 className="mt-4 font-heading text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-white leading-[0.98] max-w-4xl">
            {t("ct.b1")} <span className="text-silver">{t("ct.b2")}</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/quote" data-testid="cta-quote-button" className="btn-light">
              {t("hero.cta1")} <ArrowRight size={16} />
            </Link>
            <a href={SITE.phone1Href} data-testid="cta-call-button" className="btn-outline-navy">
              {t("ct.call")} {SITE.phone1}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  useEffect(() => {
    document.title = "Sana Steel Industries | Scaffolding & Formwork Manufacturer in Bengaluru";
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <Pillars />
      <Highlights />
      <IdealFor />
      <CtaBand />
    </main>
  );
}
